/**
 * <lookbook-flip> — the flip and the counter for the lookbook's spreads.
 *
 * Without JavaScript the track is a horizontal scroll with snap points, so every spread is still
 * reachable. Once this element runs, the pages stack in one place and a flip cross-fades from one
 * to the next, as live's does (SAVETTE-65; live is an Embla carousel with its Fade plugin, whose
 * curve the CSS in sections/lookbook.liquid reproduces). This element drives the flip: the halves
 * of the screen live flips by (a click on the left goes back, on the right forward), a swipe, a
 * sideways trackpad gesture, the arrow keys, and a count a screen reader can hear. There are no
 * visible controls: live has none, and that is the direction on SAVETTE-48.
 *
 * Only the page showing, any page fading out, the page before it and the one after it draw their
 * images — on a wide screen the two after it — and the rest keep theirs undrawn, so lazy loading
 * still fetches only the pages near the one shown (mark() has why, and which of them wait).
 *
 * The End page and the Index (SAVETTE-48, Kylie 8 Oct) close the track with links that jump to a
 * page of it — Back to Beginning, the Index button, every Index thumbnail. The jump goes through
 * the same easing as a flip, and focus lands on the page jumped to (on the Index, its title), so
 * the next Tab carries on from there. Without this script only the Index link still works, as a
 * plain link to the Index page; Back to Beginning just brings the lookbook into view.
 *
 * A swipe is read from touch events, not pointer events (SAVETTE-70: tapping flipped on a phone,
 * swiping did not). The track leaves vertical panning to the browser, and a real thumb never
 * travels perfectly sideways: the moment its few pixels of drift start the page's vertical
 * scroll, the browser takes the pointer and fires pointercancel instead of pointerup, so the
 * swipe was never seen. Touch events keep coming while the page scrolls, and end with touchend
 * where the finger lifted.
 *
 * The End page's other lookbooks are a row that scrolls sideways on its own on a phone
 * (data-lookbook-scroller). A swipe, a sideways wheel or a tap there belongs to the row; the
 * lookbook flips only from outside it, and anywhere once the row fits and stops scrolling.
 *
 * The Index's thumbnails are built here, from the spreads on the page in track order. A section
 * is told nothing about its theme blocks (docs/manual-qa-log.md, SAVETTE-50), so each spread
 * carries its own thumbnail and marks its first pane; a spread hidden in the editor is not on the
 * page and one with nothing in it has no pane, so neither is counted and the numbers stay true.
 *
 * On a wide screen with a mouse it also brings the lookbook into full view (SAVETTE-66), as live's
 * /lookbooks pages do: 800ms after it mounts the page eases down to the top of the spreads so the
 * header and banner scroll off and the spread fills the screen, and every flip by click or arrow
 * key eases it back there if the shopper has scrolled the page since. A swipe, a drag or a wheel
 * on the spreads never moves the page, and nothing moves on a phone, a tablet or below 800px.
 * The mechanics — the delay, the guards, the easing — are in immersion.js, shared with the
 * Process and Care Guide pages (SAVETTE-70); this element says only what it eases to (the
 * track) and when a flip calls for it, and loads it from the URL its section gives it
 * (loadImmersion).
 */
/** The cross-fade's length, as the CSS has it: how long a page stays drawn while it fades out. */
const FADE_MS = 700;
/** The longest a jump to a far page waits for its images before it fades in regardless. */
const PRELOAD_WAIT = 1000;
/** How far a finger has to travel sideways, more than it does up or down, to count as a swipe. */
const SWIPE_MIN = 40;
/** A sideways trackpad gesture: how far it travels to flip, and the pause that ends it. */
const WHEEL_MIN = 30;
const WHEEL_QUIET = 200;

/**
 * Which way a finger's travel flips: 1 for the next page (it went left), -1 for the one before,
 * 0 when it was no swipe — too short, or more up or down than sideways, which is the page's own
 * scroll.
 */
function swipeStep(dx, dy) {
  if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}

class LookbookFlip extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lookbook-track]');
    this.count = this.querySelector('[data-lookbook-count]');
    if (!this.track) return;

    // The stacked, cross-fading track takes over from the scrolling one, on the page the track
    // was scrolled to (nearly always the first).
    this.leaving = new Map();
    this.current = this.pages[this.scrolledIndex()];
    this.track.scrollLeft = 0;
    this.toggleAttribute('data-fade-init', true);
    this.toggleAttribute('data-fade', true);

    // live flips by clicking the halves of the screen; read that off the track itself, because an
    // overlay either sits under the track and is never tapped or over it and eats the swipe
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    this.track.addEventListener('click', this.onTrackClick);
    this.track.addEventListener('click', this.onJump);
    this.track.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.buildIndex();
    this.mark();
    this.update();
    // the pages take their first states with no fade (lookbook.liquid, data-fade-init): reading
    // layout settles them now, so letting the fades back in does not animate them
    void this.offsetWidth;
    this.removeAttribute('data-fade-init');

    this.loadImmersion();
  }

  /**
   * immersion.js comes from its versioned asset URL, which only the section knows
   * (data-immersion-src; why it is not a static import is in immersion.js). The arrival counts
   * from this moment, as it did when the mechanics lived here: the import only hands connect()
   * the time and scroll position noted now. Until it resolves a flip simply does not re-align,
   * and if the lookbook leaves the page first, nothing is attached.
   */
  loadImmersion() {
    const src = this.dataset.immersionSrc;
    if (!src) return;
    const arrival = { at: performance.now(), scrollY: window.scrollY };
    const load = (this.immersionLoad = {});
    // only a failed import is swallowed (the page then simply stays put); anything connect()
    // throws is a bug and surfaces
    import(src).then(
      ({ Immersion }) => {
        if (this.immersionLoad !== load || !this.isConnected) return;
        this.immersion = new Immersion(() => this.track);
        this.immersion.connect(arrival);
      },
      () => {},
    );
  }

  disconnectedCallback() {
    this.track?.removeEventListener('keydown', this.onKeydown);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('touchstart', this.onTouchStart);
    this.track?.removeEventListener('touchmove', this.onTouchMove);
    this.track?.removeEventListener('touchend', this.onTouchEnd);
    this.track?.removeEventListener('touchcancel', this.onTouchEnd);
    this.swipe = null;
    this.track?.removeEventListener('click', this.onTrackClick);
    this.track?.removeEventListener('click', this.onJump);
    this.track?.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.leaving?.forEach((timer) => clearTimeout(timer));
    this.leaving?.clear();
    this.immersionLoad = null;
    this.immersion?.disconnect();
    this.immersion = null;
  }

  /**
   * What counts as a page depends on the viewport: a spread on a wide screen, a single image on a
   * phone, where the spread steps aside and its panes become screens of their own. Reading which
   * elements actually have a box keeps this in step with the CSS rather than duplicating its
   * breakpoint here.
   */
  get pages() {
    // An element with display: contents has no box of its own, so it can be neither scrolled to
    // nor measured — and there are two of them stacked on a phone, Shopify's block wrapper and
    // the spread inside it. Keep descending until something with a box turns up.
    // Nothing that is not drawn is a page either: each spread keeps its Index thumbnail in a
    // <template>, which is display: none.
    const flatten = (nodes) =>
      nodes.flatMap((node) => {
        const display = getComputedStyle(node).display;
        if (display === 'none') return [];
        return display === 'contents' ? flatten([...node.children]) : [node];
      });
    return flatten([...this.track.children]);
  }

  /** Where the scrolling track sat before the stacked one took over: the page nearest its start. */
  scrolledIndex() {
    const left = this.track.scrollLeft;
    let nearest = 0;
    let best = Infinity;
    this.pages.forEach((page, i) => {
      const distance = Math.abs(page.offsetLeft - this.track.offsetLeft - left);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    return nearest;
  }

  /**
   * The position of the page showing. Across the breakpoint the pages change (a spread on a wide
   * screen, its panes on a phone), so the page showing is found again from the one it was: the
   * spread that holds it, or the spread's first pane.
   */
  get index() {
    if (!this.current) return 0;
    const position = this.pages.indexOf(this.current);
    return position >= 0 ? position : Math.max(this.pageOf(this.current), 0);
  }

  go(step) {
    this.goTo(this.index + step);
  }

  /**
   * Show the page at this position in the track, clamped to the first and last. Resolves true once
   * it shows, or false if a later flip took its place first.
   *
   * A page far from the one showing (an Index jump) has its images undrawn, so it would fade in
   * blank: it and its neighbors are drawn first, and the swap waits for its images, but never
   * longer than PRELOAD_WAIT.
   */
  goTo(position) {
    const pages = this.pages;
    const target = pages[Math.min(Math.max(position, 0), pages.length - 1)];
    if (!target) return Promise.resolve(false);
    const token = {};
    this.pending = token;
    if (target.dataset.lookbookSlide !== 'far') {
      this.show(target);
      return Promise.resolve(true);
    }
    this.preloading = target;
    this.mark();
    const images = [...target.querySelectorAll('img')].map((img) => img.decode().catch(() => {}));
    const wait = new Promise((resolve) => setTimeout(resolve, PRELOAD_WAIT));
    return Promise.race([Promise.all(images), wait]).then(() => {
      if (this.pending !== token || !this.isConnected) return false;
      this.show(target);
      return true;
    });
  }

  /**
   * The swap itself. The page it replaces stays drawn while it fades out, however far away it is,
   * and is let go once the fade is over. Focus inside it would be lost when it hides, and with it
   * the arrow keys, so it moves to the track first (a jump then moves it on to its landing point).
   */
  show(target) {
    const previous = this.pages[this.index];
    if (previous && previous !== target) {
      clearTimeout(this.leaving.get(previous));
      this.leaving.set(
        previous,
        setTimeout(() => {
          this.leaving.delete(previous);
          this.mark();
        }, FADE_MS),
      );
      if (previous.contains(document.activeElement)) this.track.focus({ preventScroll: true });
    }
    this.current = target;
    this.preloading = null;
    this.mark();
    this.update();
  }

  /**
   * Mark every page for the CSS: the one showing, any fading out, the pages near it and those
   * either side of a page about to be jumped to (drawn but not shown, so a flip finds their images
   * loaded) and the rest, which keep their images undrawn. An element that stopped being a page
   * when the layout changed loses its mark.
   *
   * On a wide screen near is one page back but two forward (SAVETTE-70). A lookbook is read
   * forward, and with only the next spread drawn, a second quick flip arrived on a spread whose
   * images had only just been asked for: on live its two large images took 1.2–1.6s, so it faded
   * in blank panes. Drawing two ahead has the spread after next loading while the next one is
   * looked at. Backward stays at one: going back is rarer, and lands on pages already seen. On a
   * phone a page is a single pane, so one ahead already holds the next image and two would only
   * double what a cellular connection spends ahead of the shopper; it stays at one either way.
   * Which it is comes from the pages themselves, as everywhere here: a wide screen's pages hold
   * whole spreads, a phone's are the panes inside them.
   *
   * The extra page must not slow the ones that matter, so each near page's images get a fetch
   * priority (prioritize): the page showing, and a page being jumped to, ask for high; the next
   * page keeps the browser's default, as it had before; the page two ahead and the one behind ask
   * for low. Near pages sit hidden in the same grid cell as the one showing, inside the viewport,
   * and a browser that raises images in the viewport would otherwise fetch them all as urgently.
   * The Index's thumbnails are left as they are while it is merely near: they are small, and a
   * flip to the Index wants them.
   */
  mark() {
    const pages = this.pages;
    const at = this.index;
    const preload = this.preloading ? pages.indexOf(this.preloading) : -1;
    const reach = pages.some((page) => page.querySelector('[data-lookbook-spread]')) ? 2 : 1;
    this.current = pages[at];
    this.track.querySelectorAll('[data-lookbook-slide]').forEach((element) => {
      if (!pages.includes(element)) element.removeAttribute('data-lookbook-slide');
    });
    pages.forEach((page, i) => {
      const ahead = i - at;
      const target = i === preload;
      let state = 'far';
      if (i === at) state = 'current';
      else if (this.leaving.has(page)) state = 'leaving';
      else if (ahead >= -1 && ahead <= reach) state = 'near';
      else if (preload >= 0 && Math.abs(i - preload) <= 1) state = 'near';
      page.dataset.lookbookSlide = state;

      let priority = null;
      if (state === 'current' || target) priority = 'high';
      else if (state === 'near' && !page.querySelector('[data-lookbook-index-grid]')) {
        priority = ahead === 2 || ahead === -1 ? 'low' : 'auto';
      }
      if (priority) this.prioritize(page, priority);
    });
  }

  /**
   * Set every image on a page to this fetch priority (mark has which). It is set in the same task
   * as the mark, before the mark's styles draw the page, so a lazy image is requested at that
   * priority from the start. A browser need not re-prioritize a request already in flight, so a
   * page that comes to show mid-download may keep the priority it was asked for at; the page two
   * ahead was asked for earlier, which is what makes up for it. On the first spread, high repeats
   * what its markup already asks for (blocks/lookbook-spread.liquid).
   */
  prioritize(page, priority) {
    page.querySelectorAll('img').forEach((img) => {
      if (img.getAttribute('fetchpriority') !== priority) img.setAttribute('fetchpriority', priority);
    });
  }

  /**
   * The position of the page that holds an element, or is held by it: on a wide screen a spread's
   * page is the block wrapper around it, on a phone the spread steps aside and its first pane is
   * the page a jump to it should land on.
   */
  pageOf(element) {
    return this.pages.findIndex(
      (page) => page === element || page.contains(element) || element.contains(page),
    );
  }

  /**
   * A link to a page of this lookbook: Back to Beginning (`data-lookbook-goto="start"`) goes to
   * the first page, any other goes to the page holding the element its #id names. A modified
   * click (a new tab, say) is left to the browser.
   *
   * Focus follows the jump rather than staying on a link that has just scrolled out of view: onto
   * the page's marked landing point (the Index's title), else the page itself — the spread on a
   * wide screen, its first pane on a phone — so the next Tab carries on from there and the arrow
   * keys still flip, their keydown reaching the track from inside it.
   */
  onJump = (event) => {
    const link = event.target.closest('a[data-lookbook-goto]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    let position = 0;
    if (link.dataset.lookbookGoto !== 'start') {
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id ? document.getElementById(id) : null;
      if (!target || !this.track.contains(target)) return;
      position = this.pageOf(target);
    }
    const page = this.pages[position];
    if (!page) return;

    event.preventDefault();
    this.immersion?.immerse();
    // focus lands once the page shows: a hidden page cannot take it
    this.goTo(position).then((shown) => {
      if (!shown) return;
      const landing = page.querySelector('[data-lookbook-focus]') ?? page;
      if (!landing.hasAttribute('tabindex')) landing.tabIndex = -1;
      landing.focus({ preventScroll: true });
    });
  };

  /**
   * Fill the Index with one thumbnail per spread, in the order the spreads sit in the track. A
   * spread with no first pane draws nothing, so it is no page and gets no number. The numbers
   * read 01, 02… and the link's name says the same ("Go to spread 01").
   */
  buildIndex() {
    const grid = this.querySelector('[data-lookbook-index-grid]');
    const item = this.querySelector('template[data-lookbook-index-item]');
    if (!grid || !item) return;

    const firstPanes = [...this.track.querySelectorAll('[data-lookbook-spread]')]
      .map((spread) => ({ spread, pane: spread.querySelector('[data-lookbook-page][id]') }))
      .filter(({ pane }) => pane);

    const items = firstPanes.map(({ spread, pane }, i) => {
      const number = String(i + 1).padStart(2, '0');
      const fragment = item.content.cloneNode(true);
      const link = fragment.querySelector('a');
      link.href = `#${pane.id}`;
      link.dataset.analyticsPropPage = String(i + 1);
      const thumb = spread.querySelector('template[data-lookbook-thumb]');
      if (thumb) fragment.querySelector('[data-lookbook-index-thumb]').append(thumb.content.cloneNode(true));
      fragment.querySelector('[data-lookbook-index-number]').textContent = number;
      fragment.querySelector('[data-lookbook-index-label]').textContent = (grid.dataset.label ?? '').replace(
        '[number]',
        number,
      );
      return fragment;
    });
    grid.replaceChildren(...items);

    // the desktop grid sizes its thumbnails so every row of four fits on the screen
    const body = grid.closest('[data-lookbook-index-body]');
    body?.style.setProperty('--index-rows', String(Math.max(1, Math.ceil(items.length / 4))));
  }

  /** Where every press began, so a click can tell a tap from a drag (onTrackClick). */
  onPointerDown = (event) => {
    this.pressedAt = { x: event.clientX, y: event.clientY };
  };

  /**
   * A row inside a page that scrolls sideways on its own (the End page's other lookbooks on a
   * phone), holding this element — but only while it overflows: once the row fits, as on a wide
   * screen, it scrolls nowhere and the lookbook flips there as anywhere else.
   */
  scrollerAt(target) {
    const row = target?.closest?.('[data-lookbook-scroller]');
    return row && row.scrollWidth > row.clientWidth ? row : null;
  }

  /**
   * A swipe flips: one finger that travelled sideways further than it did up or down, the way
   * it went — to the left for the next page (swipeStep). Up and down stay the page's own scroll,
   * and the track allows only vertical panning, so the browser never moves it sideways. A second
   * finger makes it a pinch, and a swipe that starts in a sideways row is the row's; both are
   * let go. The page flips on release rather than following the finger: a flip is a cross-fade,
   * with no sideways position to drag. A mouse flips by its click, as on live's desktop; so does a
 * pen that sends no touch events (a Windows stylus): it taps through, it does not swipe.
   *
   * The finger's last position is kept as it moves, so a touchcancel (the system taking the
   * touch) ends the swipe where the finger was last seen, as a touchend does where it lifted.
   */
  onTouchStart = (event) => {
    const [touch] = event.changedTouches;
    if (event.touches.length > 1 || !touch || this.scrollerAt(event.target)) {
      this.swipe = null;
      return;
    }
    const { identifier: id, clientX: x, clientY: y } = touch;
    this.swipe = { id, x, y, lastX: x, lastY: y };
  };

  onTouchMove = (event) => {
    const touch = this.swipeTouch(event);
    if (!touch) return;
    this.swipe.lastX = touch.clientX;
    this.swipe.lastY = touch.clientY;
  };

  onTouchEnd = (event) => {
    const touch = this.swipeTouch(event);
    if (!touch) return;
    const swipe = this.swipe;
    this.swipe = null;
    const x = event.type === 'touchcancel' ? swipe.lastX : touch.clientX;
    const y = event.type === 'touchcancel' ? swipe.lastY : touch.clientY;
    const step = swipeStep(x - swipe.x, y - swipe.y);
    if (step) this.go(step);
  };

  /** The swipe's own finger among those this touch event changed, if a swipe is under way. */
  swipeTouch(event) {
    if (!this.swipe) return null;
    return [...event.changedTouches].find((touch) => touch.identifier === this.swipe.id) ?? null;
  }

  /**
   * A sideways trackpad gesture (or a sideways wheel) flips once per gesture, however long its
   * momentum runs: a pause ends the gesture. An up or down scroll is left to the page, and a
   * sideways one over a row that scrolls sideways is left to the row.
   */
  onWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    if (this.scrollerAt(event.target)) return;
    event.preventDefault();
    const now = performance.now();
    if (now - (this.wheelAt ?? -Infinity) > WHEEL_QUIET) {
      this.wheelTravel = 0;
      this.wheelFlipped = false;
    }
    this.wheelAt = now;
    if (this.wheelFlipped) return;
    this.wheelTravel += event.deltaX;
    if (Math.abs(this.wheelTravel) < WHEEL_MIN) return;
    this.wheelFlipped = true;
    this.go(this.wheelTravel > 0 ? 1 : -1);
  };

  /** In the theme editor, selecting a spread in the sidebar shows it. */
  onBlockSelect = (event) => {
    if (!this.track.contains(event.target)) return;
    const position = this.pageOf(event.target);
    if (position >= 0) this.goTo(position);
  };

  /**
   * A click on the left half goes back, the right half forward — but only a click. A swipe ends
   * in a click event too, and flipping a second spread because someone dragged would be maddening,
   * so a pointer that travelled is left alone. A tap in the gaps of a sideways row is the row's.
   */
  onTrackClick = (event) => {
    if (event.target.closest('a, button') || this.scrollerAt(event.target)) return;
    const from = this.pressedAt;
    if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > 10) return;
    const box = this.track.getBoundingClientRect();
    this.go(event.clientX - box.left < box.width / 2 ? -1 : 1);
    this.immersion?.immerse();
  };

  onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    this.go(event.key === 'ArrowRight' ? 1 : -1);
    this.immersion?.immerse();
  };

  /** A resize can cross the breakpoint, where the pages change: mark them again. */
  onResize = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.mark();
      this.update();
    });
  };

  update() {
    const total = this.pages.length;
    const at = this.index;
    if (this.count) this.count.textContent = `${at + 1} / ${total}`;
  }
}

if (!customElements.get('lookbook-flip')) customElements.define('lookbook-flip', LookbookFlip);
